import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { BarChart3, Users, FileText, Package, IndianRupee, TrendingUp, MapPin, Activity } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#0d9488', '#f59e0b', '#6366f1', '#ec4899', '#10b981', '#8b5cf6'];

const TeamDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/admin/dashboard`);
        setData(res.data);
      } catch (err) {
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div></div>;
  if (!data) return <p className="text-center text-slate-500 py-20">Failed to load dashboard data</p>;

  const { totals, statusBreakdown, typeBreakdown, monthlyTrend, topDestinations, recentTrips } = data;

  const statusData = Object.entries(statusBreakdown).map(([k, v]) => ({ name: k || 'Unknown', value: v }));
  const typeData = Object.entries(typeBreakdown).map(([k, v]) => ({ name: k || 'Unknown', value: v }));
  const trendData = (monthlyTrend || []).map(m => ({ month: m._id, trips: m.count, revenue: Math.round(m.revenue / 1000) }));

  return (
    <div className="space-y-6" data-testid="team-dashboard-page">
      <div>
        <h1 className="text-3xl font-heading font-bold text-slate-900 flex items-center gap-3">
          <BarChart3 className="h-8 w-8 text-primary-700" />Team Dashboard
        </h1>
        <p className="text-slate-500 mt-1">Business overview and analytics</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <MetricCard icon={FileText} label="Total Trips" value={totals.trips} color="teal" />
        <MetricCard icon={Users} label="Travellers" value={totals.travellers} color="sky" />
        <MetricCard icon={Package} label="Itineraries" value={totals.itineraries} color="violet" />
        <MetricCard icon={TrendingUp} label="This Month" value={totals.tripsThisMonth} color="amber" />
        <MetricCard icon={IndianRupee} label="Total Revenue" value={`${(totals.totalRevenue / 100000).toFixed(1)}L`} color="emerald" />
        <MetricCard icon={IndianRupee} label="Monthly Rev." value={`${(totals.monthlyRevenue / 100000).toFixed(1)}L`} color="rose" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Trend */}
        <Card className="p-6 bg-white rounded-2xl shadow-sm" data-testid="monthly-trend-chart">
          <h3 className="font-semibold text-slate-900 mb-4">Monthly Trend</h3>
          {trendData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="trips" fill="#0d9488" radius={[4, 4, 0, 0]} name="Trips" />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 text-center py-10">No data yet</p>}
        </Card>

        {/* Status Breakdown */}
        <Card className="p-6 bg-white rounded-2xl shadow-sm" data-testid="status-breakdown-chart">
          <h3 className="font-semibold text-slate-900 mb-4">Trip Status Breakdown</h3>
          {statusData.length > 0 ? (
            <div className="flex items-center gap-6">
              <ResponsiveContainer width="50%" height={200}>
                <PieChart>
                  <Pie data={statusData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={false}>
                    {statusData.map((_, i) => <Cell key={`status-cell-${i}`} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2">
                {statusData.map((s, i) => (
                  <div key={`status-${s.name}`} className="flex items-center gap-2 text-sm">
                    <div className="w-3 h-3 rounded-full" style={{ background: COLORS[i % COLORS.length] }}></div>
                    <span className="text-slate-600">{s.name}: <strong>{s.value}</strong></span>
                  </div>
                ))}
              </div>
            </div>
          ) : <p className="text-sm text-slate-400 text-center py-10">No data yet</p>}
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Destinations */}
        <Card className="p-6 bg-white rounded-2xl shadow-sm" data-testid="top-destinations">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2"><MapPin className="h-4 w-4 text-teal-600" />Top Destinations</h3>
          <div className="space-y-3">
            {topDestinations.map((d, i) => (
              <div key={`dest-${d.name}`} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center text-xs font-bold text-teal-700">{i + 1}</span>
                  <span className="font-medium text-slate-800 text-sm">{d.name}</span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-semibold text-slate-900">{d.count} trips</span>
                  <span className="text-xs text-slate-500 ml-2">{(d.revenue / 1000).toFixed(0)}K</span>
                </div>
              </div>
            ))}
            {topDestinations.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No data yet</p>}
          </div>
        </Card>

        {/* Recent Activity */}
        <Card className="p-6 bg-white rounded-2xl shadow-sm" data-testid="recent-activity">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2"><Activity className="h-4 w-4 text-amber-600" />Recent Activity</h3>
          <div className="space-y-3 max-h-[280px] overflow-y-auto">
            {recentTrips.map((t, i) => (
              <div key={t.tripId || `trip-${i}`} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                <div>
                  <p className="text-sm font-medium text-slate-800">{t.guestName || 'Guest'}</p>
                  <p className="text-xs text-slate-500">{t.destination} &middot; {t.displayTripId || t.tripId}</p>
                </div>
                <div className="text-right">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${t.status === 'Confirmed' ? 'bg-emerald-100 text-emerald-700' : t.status === 'Cancelled' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                    {t.status || 'Draft'}
                  </span>
                </div>
              </div>
            ))}
            {recentTrips.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No activity yet</p>}
          </div>
        </Card>
      </div>
    </div>
  );
};

const COLOR_MAP = {
  teal: { bg: 'bg-teal-100', text: 'text-teal-600' },
  sky: { bg: 'bg-sky-100', text: 'text-sky-600' },
  violet: { bg: 'bg-violet-100', text: 'text-violet-600' },
  amber: { bg: 'bg-amber-100', text: 'text-amber-600' },
  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600' },
  rose: { bg: 'bg-rose-100', text: 'text-rose-600' },
};

const MetricCard = ({ icon: Icon, label, value, color }) => {
  const c = COLOR_MAP[color] || COLOR_MAP.teal;
  return (
    <Card className="p-4 bg-white rounded-2xl shadow-sm" data-testid={`metric-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className={`w-10 h-10 ${c.bg} rounded-xl flex items-center justify-center mb-2`}>
        <Icon className={`h-5 w-5 ${c.text}`} />
      </div>
      <p className="text-2xl font-bold text-slate-900">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </Card>
  );
};

export default TeamDashboard;
